import { useRef } from 'react'
import { toast } from 'sonner'
import { logger } from '@/lib/logger'
import { decodeJWT } from '@/utils/jwt-decoder'
import { saveTokens, saveUser } from '@/utils/token-storage'
//import { useNavigate } from '@tanstack/react-router'
import { queryClient } from '@/context/react-query-provider'
import {
  useLogin,
  type LoginResponse,
} from '@/features/auth/hooks/query/use-auth'
import { getDealerByEmail } from '@/features/auth/services/auth.service'
import { recordUserLoginDetail } from '@/features/auth/services/login-detail.service'
import { homeTabItems } from '@/features/home/constants/data'
import { useFetchDashboardUser } from '@/features/home/hooks/use-dashboard-user'

interface UseLoginHandlerOptions {
  redirectTo?: string
}

export function useLoginHandler(_options: UseLoginHandlerOptions = {}) {
  // Store rememberMe in a ref so it can be accessed in the success callback
  const rememberMeRef = useRef<boolean>(true)
  //const navigate = useNavigate()
  const { fetchDashboardUser } = useFetchDashboardUser()

  const handleLoginSuccess = async (
    loginResponse: LoginResponse,
    email: string,
    dealerId: string
  ) => {
    const { username, employee_id, tokens } = loginResponse
    const userEmail = username.split('#')[0] || email

    let isSuperAdmin = false
    try {
      const dealerInfo = await getDealerByEmail(userEmail)
      isSuperAdmin = dealerInfo?.is_super_admin === true
    } catch {
      // Non-fatal: proceed with normal employee_id from login response
    }

    const storedEmployeeId = isSuperAdmin ? 'SUPER_ADMIN' : employee_id

    // Persist tokens and user before any redirect; do not navigate until safely stored
    try {
      saveTokens(tokens, rememberMeRef.current)
      const userInfo = decodeJWT(tokens.id_token)
      const org_user_id = userInfo?.['custom:org_user_id']
      saveUser(
        {
          username,
          employee_id: storedEmployeeId,
          dealer_id: dealerId,
          is_super_admin: isSuperAdmin,
          ...(org_user_id && !isSuperAdmin && { org_user_id }),
        },
        rememberMeRef.current
      )
    } catch (err) {
      logger.error({
        message: 'Login: failed to persist tokens',
        category: 'api',
        error: err,
      })
      return
    }

    // Invalidate screen access control so filters load from API for the new user
    void queryClient.invalidateQueries({ queryKey: ['screen-access-control'] })

    logger.info({
      message: 'Login success',
      category: 'api',
      extra: { username: userEmail },
    })

    let displayName = userEmail
    if (!isSuperAdmin) {
      try {
        const dashboardUser = await fetchDashboardUser(employee_id)

        if (dashboardUser?.full_name && dashboardUser.full_name.trim()) {
          displayName = dashboardUser.full_name.trim()
        }
      } catch (_error) {
        // Failed to fetch user details - using email as fallback
      }
    }

    toast.success(`Welcome back, ${displayName} !`)

    try {
      const now = new Date().toISOString()
      const deviceInfo = `${navigator.platform}, ${navigator.userAgent}`
      const orgUserId =
        loginResponse.username || loginResponse.employee_id || null

      await recordUserLoginDetail(
        {
          login_datetime: now,
          logout_datetime: null,
          device_information: deviceInfo,
          source: 'web',
        },
        {
          dealer_id: dealerId,
          employee_id: storedEmployeeId,
        }
      )

      // cache for logout use
      localStorage.removeItem('userLoginDetailMeta')
      localStorage.setItem(
        'userLoginDetailMeta',
        JSON.stringify({
          orgUserId,
          dealerId,
          employeeId: storedEmployeeId,
          isSuperAdmin,
        })
      )

      // initialize tab order for new session (cleared on logout)
      localStorage.removeItem('homeTabOrder')
      localStorage.setItem('homeTabOrder', JSON.stringify(homeTabItems))
    } catch (_err) {
      // eslint-disable-next-line no-console
      console.error('Failed to record user login detail:', _err)
    }

    // Navigate only after tokens are stored; redirect to dashboard
    setTimeout(() => {
      window.location.href = '/new-vehicle/lead-performance'
    }, 500)
  }

  const { mutate: loginMutation, isLoading } = useLogin({
    onSuccess: (data, variables) => {
      void handleLoginSuccess(data, variables.username, variables.dealer_id)
    },
  })

  // Wrapper function to accept rememberMe parameter
  const login = (credentials: {
    username: string
    password: string
    dealer_id: string
    rememberMe?: boolean
  }) => {
    // Store rememberMe in ref before calling mutation
    rememberMeRef.current = credentials.rememberMe ?? true
    const { rememberMe, ...rest } = credentials
    // Trim email and password (start, end, and any spaces in middle) before sending to API
    const loginRequest = {
      ...rest,
      username: rest.username.trim().replace(/\s+/g, ''),
      password: rest.password.trim().replace(/\s+/g, ''),
    }
    loginMutation(loginRequest)
  }

  return {
    login,
    isLoading,
  }
}
